/**
 * A gate selected on the canvas (Feature 72, 4.2): what decides whether the
 * step after it runs. Pass automatically, off on a new link, lets it pass
 * without reading its conditions, which it keeps (D8). Otherwise the step
 * runs only when the conditions hold, all or any of them; a gate with no
 * condition and no auto pass is empty, never passes, and a save refuses it
 * (D3). A condition reads the node before the gate first, or anything
 * upstream of it (D5). A condition on a list may be a list test, whose item
 * conditions read one item of the list (Feature 77 D21).
 *
 * Add else if and Add else grow the gate into a decision that chooses one
 * path (Feature 79 D3): its conditions become the decision's first branch.
 */
import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { Inline, Typography } from '../../ui/finixt.js';
import { Toggle } from '../../ui/Toggle.js';
import { grow } from './decisions.js';
import { disconnect, linkId, setAutoPass, setRules, type Draft } from './draft.js';
import { InspectorPanel } from './InspectorPanel.js';
import { RulesEditor } from './RulesEditor.js';
import type { Schemas } from './references.js';
import type { Selection } from './selection.js';

export function LinkInspector({
  draft,
  from,
  to,
  schemas,
  onChange,
  onSelect,
}: {
  draft: Draft;
  from: string;
  to: string;
  schemas: Schemas;
  onChange: (draft: Draft) => void;
  onSelect: (selection: Selection) => void;
}) {
  const link = draft.links.find((l) => l.from === from && l.to === to);
  if (!link) return null;
  const items = link.rules.items;
  const auto = Boolean(link.auto_pass);
  /** Grow the gate into a decision, and open it. */
  const growInto = (kind: 'else' | 'else_if') => {
    const grown = grow(draft, from, to, kind);
    if (grown.name === null) return;
    onChange(grown.draft);
    onSelect({ kind: 'node', id: grown.name });
  };

  const remove = (
    <Button
      variant="danger"
      size="sm"
      onClick={() => {
        onChange(disconnect(draft, from, to));
        onSelect(null);
      }}
    >
      Remove link
    </Button>
  );
  return (
    <InspectorPanel>
      <Typography variant="eyebrow">Gate link</Typography>
      <span className="mono" style={{ fontWeight: 'var(--weight-semibold)' }}>
        {linkId(from, to)}
      </span>
      <Typography variant="meta">
        Decides whether <span className="mono">{to}</span> runs once{' '}
        <span className="mono">{from}</span> has ended.
      </Typography>
      <Field
        id="gate-auto"
        label="Pass automatically"
        hint={
          auto
            ? 'Passes without reading its conditions; they are kept for when you turn this off.'
            : `${to} runs only when its conditions hold.`
        }
      >
        <Toggle
          checked={auto}
          aria-label="Pass automatically"
          onChange={(on) => onChange(setAutoPass(draft, from, to, on))}
        />
      </Field>
      {!auto && items.length === 0 && (
        <Typography variant="meta" style={{ color: 'var(--warn)' }}>
          Empty: add a condition, or turn Pass automatically on. A save refuses an empty gate.
        </Typography>
      )}
      <RulesEditor
        draft={draft}
        from={from}
        rules={link.rules}
        schemas={schemas}
        id="gate"
        dimmed={auto}
        note={
          auto &&
          items.length > 0 && (
            <Typography variant="meta">Not in use while Pass automatically is on.</Typography>
          )
        }
        onChange={(rules) => onChange(setRules(draft, from, to, rules))}
      />
      <Inline gap={7} wrap={false}>
        <Button variant="secondary" size="sm" onClick={() => growInto('else_if')}>
          Add else if
        </Button>
        <Button variant="secondary" size="sm" onClick={() => growInto('else')}>
          Add else
        </Button>
      </Inline>
      <Typography variant="meta">
        Choose one path instead: the gate link becomes a gate node, its conditions the first
        condition node.
      </Typography>
      {remove}
    </InspectorPanel>
  );
}
